import { useState, useEffect, useRef } from "react";
import "./../App.css";
import { Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Request } from "../api/request";
import QuestionScreen from "./QuestionScreen";
import {
  ClearLocalStorage,
  GetDataFromLocalStorage,
  QuizFirstQuestion,
  StoreDataInLocalStorage,
} from "../utils/storeAndGetDataFromLocalStorage";
import {
  FireAnalyticsEvent,
  FireTealiumEvent,
  SendNFTEvents,
} from "../utils/events";
import {
  BASED_ON_CONCERN_BUTTON,
  BASED_ON_PRODUCT_BUTTON,
  HEADER_TEXT,
  HOME_PAGE_TITLE_TEXT,
  HOME_SCREEN_QUESTION,
  QUIZ_TYPE,
} from "../utils/constants";
import Toaster from "./Toaster";

const ColorButton = styled(Button)(({ theme }) => ({
  color: "#fff",
  fontWeight: "600",
  backgroundColor: "#E90C5A",
  "&:hover": {
    backgroundColor: "#C00B4B",
  },
  "@media only screen and (max-width: 700px)": {
    padding: "2px 7px",
    lineHeight: "2",
  },
}));

function HomeScreen() {
  const [showQuestion, setShowQuestion] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const whiteCardRef = useRef(null);
  const [anyError, setAnyError] = useState("");

  useEffect(() => {
    // Call the function initially to get the size.
    getElementHeight();

    // Whenever the white card height increase or element add into that,
    // observe that height change and update the orange card height.
    window.addEventListener("resize", getElementHeight);

    // When a user reloads a tab while answering a question,
    // redirect to the question they were previously viewing.
    const previousQuestionResponse = GetDataFromLocalStorage(
      "previousQuestionResponse"
    );
    if (previousQuestionResponse) {
      // if we have already data in the local storage means user has already started the quiz before. So redirect user to where user left.
      setApiResponse(previousQuestionResponse.content);
      setShowQuestion(true);
    } else {
      //when previous question is not present on the local storage that means on initial page
      // Tealium page view events
      FireTealiumEvent(
        null,
        {
          page_name: "skincare quiz",
          page_type: "marketing",
          page_group: "discover",
          quiz_type: "skincare",
        },
        true
      );
    }

    // Cleanup function to remove the event listener.
    return () => {
      window.removeEventListener("resize", getElementHeight);
    };
  }, []);

  // To set the dynamic height of the orange card.
  const getElementHeight = () => {
    if (whiteCardRef.current) {
      const height = whiteCardRef.current.clientHeight;
      document.querySelector(".orange-card").style.height = height + "px";
    }
  };

  async function startQuiz(answer) {
    try {
      const firstQuestion = QuizFirstQuestion(answer);
      const response = await Request({
        data: firstQuestion,
        method: "POST",
      });

      if (response) {
        const data = {
          quizz_type: QUIZ_TYPE,
          content: response.batch.content,
        };

        StoreDataInLocalStorage("previousQuestionResponse", data);
        StoreDataInLocalStorage("previousQuestionPayload", firstQuestion);
        setApiResponse(response.batch.content);
        setShowQuestion(true);
        SendNFTEvents(response.batch.content);

        //Google Analytics event
        FireAnalyticsEvent("guideVBAStart", {
          guideVBAQuizType: "skincare",
          guideVBAQuizQuestion: HOME_SCREEN_QUESTION,
          guideVBAQuizAnswer: answer,
        });

        // Tealium event
        FireTealiumEvent("quiz_start", { quiz_type: "skincare" });
      }
    } catch (err) {
      console.log("Something went wrong while starting the quiz", err);
      setAnyError("Something went wrong while starting the quiz");
    }
  }

  const endQuiz = () => {
    ClearLocalStorage();
    let progress = document.getElementById("progress");
    progress.value = 0;
    setShowQuestion(false);
    StoreDataInLocalStorage("shouldHideBackButton", "true");

    // Tealium event
    let eventPayload = {
      quiz_type: "skincare",
      quiz_step: "start over",
      action_label: "start over",
      action_type: "link",
    };

    FireTealiumEvent("link_click", eventPayload);

    // Tealium page view events
    FireTealiumEvent(
      null,
      {
        page_name: "skincare quiz",
        page_type: "marketing",
        page_group: "discover",
        quiz_type: "skincare",
      },
      true
    );
  };

  const handleNextQuestion = async (answers) => {
    setAnyError();
    let previousQuestionResponse = GetDataFromLocalStorage(
      "previousQuestionResponse"
    );

    // if multi choice question then array will come in answer otherwise string will come.
    let payload = previousQuestionResponse;
    payload.content.to_update.current_answers = Array.isArray(answers)
      ? answers
      : [answers];

    try {
      const response = await Request({
        data: payload,
        method: "POST",
      });

      if (response) {
        const data = {
          quizz_type: QUIZ_TYPE,
          content: response.batch.content,
        };

        StoreDataInLocalStorage("previousQuestionResponse", data);
        // storing previous question payload to show the question when back button click.
        StoreDataInLocalStorage("previousQuestionPayload", payload);
        setApiResponse(response.batch.content);
        setShowQuestion(true);
        StoreDataInLocalStorage("shouldHideBackButton", "false");

        let progress = document.getElementById("progress");
        progress.value = response.batch.content.to_use.skus.length
          ? 100
          : response.batch.content.to_pass.answer_history.length * 10;

        SendNFTEvents(response.batch.content);

        // fire Tealium and google analytics event once the endpoints successfully triggered.
        let eventPayload = {
          quiz_type: "skincare",
          quiz_step: "response",
          quiz_question: payload.content.to_pass.current_question.toLowerCase(),
          quiz_response: payload.content.to_update.current_answers.join(", ").toLowerCase(),
        };

        //Google Analytics event
        FireAnalyticsEvent("guideVBAInProgress", {
          guideVBAQuizType: "skincare",
          guideVBAQuizQuestion: eventPayload.quiz_question,
          guideVBAQuizAnswer: eventPayload.quiz_response,
        });

        FireTealiumEvent("link_click", eventPayload);
      }
    } catch (err) {
      console.log("Something went wrong while getting the next question", err);
      setAnyError("Something went wrong while getting the next question");
    }
  };

  const handlePreviousQuestion = (previousQuestionData) => {
    const data = {
      quizz_type: QUIZ_TYPE,
      content: previousQuestionData.content,
    };

    StoreDataInLocalStorage("previousQuestionResponse", data);
    setApiResponse(previousQuestionData.content);
    setShowQuestion(true);
    StoreDataInLocalStorage("shouldHideBackButton", "true");
  };

  return (
    <div id="quiz-body">
      {anyError && (
        <Toaster openToaster={true} type={"error"} message={anyError} />
      )}
      {!showQuestion ? (
        <div className="parent-card bg-image">
          <div className="orange-card"></div>
          <div ref={whiteCardRef} className="white-card">
            <Typography className="home-page-header" variant="h3">
              {HEADER_TEXT}
            </Typography>
            <div className="home-page-sub-title">{HOME_PAGE_TITLE_TEXT}</div>
            <div>
              <Typography className="mt-1 fw-bold" variant="subtitle1">
                {HOME_SCREEN_QUESTION}
              </Typography>
              <ColorButton
                variant="contained"
                className="mt-3 text-capitalize fs-6"
                onClick={() => {
                  startQuiz(BASED_ON_CONCERN_BUTTON);
                }}
              >
                {BASED_ON_CONCERN_BUTTON}
              </ColorButton>
              <ColorButton
                variant="contained"
                className="mt-3 text-capitalize fs-6 mx-3"
                onClick={() => {
                  startQuiz(BASED_ON_PRODUCT_BUTTON);
                }}
              >
                {BASED_ON_PRODUCT_BUTTON}
              </ColorButton>
            </div>
          </div>
        </div>
      ) : (
        <QuestionScreen
          data={apiResponse}
          handleNextQuestion={handleNextQuestion}
          handlePreviousQuestion={handlePreviousQuestion}
          endQuiz={endQuiz}
        />
      )}
    </div>
  );
}
export default HomeScreen;
